import React, { ComponentPropsWithoutRef, FC, MouseEvent, useCallback } from 'react';
import { Typography } from '@rbx/ui';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import MarkdownPre from '@modules/ui/markdown/components/MarkdownPre';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import MarkdownSimpleInlineCode from '@modules/ui/markdown/components/MarkdownSimpleInlineCode';

type MarkdownHeaderComponentProps =
  | ComponentPropsWithoutRef<'h1'>
  | ComponentPropsWithoutRef<'h2'>
  | ComponentPropsWithoutRef<'h3'>
  | ComponentPropsWithoutRef<'h4'>
  | ComponentPropsWithoutRef<'h5'>
  | ComponentPropsWithoutRef<'h6'>;

type HeaderLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const getHeaderComponent = (headerLevel: HeaderLevel) => {
  const component = ({ children }: MarkdownHeaderComponentProps) => (
    <Typography
      variant={headerLevel}
      component={headerLevel}
      marginTop='1.75em'
      marginBottom='0.5em'>
      {children}
    </Typography>
  );
  component.displayName = `Header.[${headerLevel}]`;
  return component;
};

const H1: FC<React.PropsWithChildren<MarkdownHeaderComponentProps>> = getHeaderComponent('h1');
const H2: FC<React.PropsWithChildren<MarkdownHeaderComponentProps>> = getHeaderComponent('h2');
const H3: FC<React.PropsWithChildren<MarkdownHeaderComponentProps>> = getHeaderComponent('h3');
const H4: FC<React.PropsWithChildren<MarkdownHeaderComponentProps>> = getHeaderComponent('h4');
const H5: FC<React.PropsWithChildren<MarkdownHeaderComponentProps>> = getHeaderComponent('h5');
const H6: FC<React.PropsWithChildren<MarkdownHeaderComponentProps>> = getHeaderComponent('h6');
const P: FC<React.PropsWithChildren<ComponentPropsWithoutRef<'p'>>> = ({ children }) => (
  <Typography variant='body2' component='p'>
    {children}
  </Typography>
);
const A: (
  onAnchorClickLoggedParams: Record<string, string>,
) => FC<React.PropsWithChildren<ComponentPropsWithoutRef<typeof DynamicallyRouted.Link>>> = (
  onAnchorClickLoggedParams: Record<string, string> = {},
) => {
  function Component({ href, children }: ComponentPropsWithoutRef<typeof DynamicallyRouted.Link>) {
    const trackAndVisitLink = useCallback(
      (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault(); // stop link from visiting href; log first, then visit
        unifiedLoggerClient.logClickEvent({
          eventName: DocsAssistantEvent.UserClickedLinkInBotResponse,
          parameters: {
            url: href,
            contents: JSON.stringify(children),
            ...onAnchorClickLoggedParams,
          },
        });
        window.open(href, '_blank');
      },
      [children, href],
    );
    return (
      <DynamicallyRouted.Link
        onClick={trackAndVisitLink}
        variant='body1'
        href={href}
        target='_blank'>
        {children}
      </DynamicallyRouted.Link>
    );
  }
  Component.displayName = 'Markdown.A';
  return Component;
};
const Code = (props: ComponentPropsWithoutRef<typeof MarkdownSimpleInlineCode>) => (
  <MarkdownSimpleInlineCode {...props} />
);

export default {
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P,
  A,
  Pre: MarkdownPre,
  Code,
};
